<template>
    <div>
        <h3 class="py-3">Details</h3>

        <div class="py-2">
            <span class="font-weight-600">Date/Location</span>
        </div>
        
        <form novalidate @change="eventActivateUpdateFlag('DateLocation')">
            <div class="row mt-2" v-if="isCreatingSingleEvent">
                <div class="col-sm-4">
                    <label class="full" :class="{'color-6':validations.getSelectedEvent.product.$invalid}">
                        Product<span class="color-6">*</span>
                    </label>
                    <div class="d-flex align-items-center">
                        <span class="font-size-small dark" v-if="isLoadingProductList">Loading products</span>
                        <v-select 
                            v-else 
                            :options="productList" 
                            label="name" 
                            placeholder="Select a product" 
                            v-model="getSelectedEvent.helperProduct" 
                            @input="changeProductSelected">
                        </v-select>
                    </div>
                </div>
            </div>

            <div class="row mt-2">
                <div class="col-sm-5">
                    <label class="full">
                        Event Name
                    </label>
                    <input type="text" class="bg-lightest w-100" v-model="getSelectedEvent.nameAlias" :placeholder="getSelectedEvent.product? getSelectedEvent.product.name : '' ">
                </div>
            </div>

            <div class="row mt-2">
                <div class="col-sm-5">
                    <label class="full">
                        Event Date<span class="color-6">*</span>
                    </label>
                    <datepicker input-class="bg-lightest" :value="getSelectedEvent.start" @input="setEventStartDate($event)" format="dd/MM/yyyy"></datepicker>
                </div>
            </div>

            <div class="row mt-2">
                <div class="col-sm-5">
                    <label class="full">
                        Time<span class="color-6">*</span>
                    </label>
                    <timepicker format="hh:mm" :defaultTime="eventStartHour" @change="setEventStartTime"></timepicker>
                </div>

                <div class="col-sm-5">
                    <label class="full" :class="{'color-6':validations.getSelectedEvent.end.$invalid}">
                        Duration<span class="color-6">*</span>
                        <span class="color-6 fs-smaller" v-if="validations.getSelectedEvent.end.$invalid">Select a valid duration</span>
                    </label>
                    <timepicker :defaultTime="eventDuration" @change="setEventEndTime" :availableHours="durationsPossibleHours" format="hh:mm"></timepicker>
                </div>
            </div>

            <div class="row mt-2">
                <div class="col-sm-5">
                    <label class="full" :class="{'color-6':validations.getSelectedEvent.location.$invalid}">
                        Location<span class="color-6">*</span>
                        <span class="color-6 fs-smaller" v-if="validations.getSelectedEvent.location.$invalid">Enter a location</span>
                    </label>
                    <input type="text" class="bg-lightest w-100" v-model="getSelectedEvent.location" placeholder="Type event location">
                </div>
            </div>
        </form>

        <div v-if="!isCreatingSingleEvent">
            <div class="pt-4 pb-2">
                <span class="font-weight-600">Status</span>
            </div>

            <div>
                <div class="col-sm-5 pl-0 pt-2">
                    <label>Event status</label>
                    <v-select 
                        :options="statusList" 
                        label="name" 
                        class="o" 
                        placeholder="Select a status" 
                        :value="getStatusValue" 
                        @input="changeStatusSelected">
                    </v-select>
                </div>

                <form novalidate @change="eventActivateUpdateFlag('Status')">
                    <div class="col-sm-10 mt-3 pl-0" v-if="getSelectedEvent.status == statusId('Canceled')">
                        <label for="cancellationReason">
                            Cancellation reason
                            <span v-if="foundCancellationReasons" class="color-6">*</span>
                        </label>

                        <v-select
                            id="cancellationReason"
                            class="open-up-cancelation-reason"
                            v-if="filteredCancellationReasons.length > 0"
                            v-model="cancellationReasonObject"
                            :options="filteredCancellationReasons"
                            :reduce="reason => reason.id"
                            label="description" 
                            placeholder="Select a reason"
                            @input="updateCancellationReason(cancellationReasonObject)"
                            open-direction="top"
                        ></v-select>
                    
                        <label v-if="!!cancellationReasonObject && !getSelectedEvent.cancelationReasonId" class="full mt-3" style="height: 104px;">
                            Cancellation description
                            <span v-if="foundCancellationReasons" class="color-6">*</span>
                            <textarea ref="cancelationNote" class="bg-lighter w-100 mt-1" rows="2" v-model="getSelectedEvent.statusMessage" ></textarea>
                        </label>

                        <div class="mt-3">
                            <checkbox name="allotment" class="" v-model="notifyEventCancellation" @input="updateEmailNotification" value="true">Notify customers by email</checkbox>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
    import Datepicker from 'vuejs-datepicker'
    import Timepicker from '@/tools/Timepicker'
    import { mapGetters, mapActions } from 'vuex'
    import { eventFormStateHelper } from '@/store/helpers/eventFormStateHelper'
    import extendedVSelect from '@/extends/vue-select'
    import loader from '@/tools/loaders/loader1'

    import { isPerOrderAudienceValid } from '@/utils/pricingTypeHelpers'
    import { EventStatus } from '@/enums/eventStatus'

    export default {
        data() {
            return {
                storedEventDuration: null,
                eventDurationDisplayed: "02:00",
                durationsPossibleHours:['00', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'],
                isLoadingProductList: false,
                productList: [],        //used when creating new single event
                statusList: [{name: 'Active', id: 1}, {name:'Canceled', id: 2}],
                categoryList: [],       //used when creating new single event
                isLoading: true,
                possibleStatusAvailable: [],
                cancellationReasonObject: null,
				defaultOtherReasonObject: {
					description: 'Other',
					cancelationReasonType: -9999, // -9999 type doesn't exist
					id: null
				},
                notifyEventCancellation: false
            }
        },
        mixins: [],
        props: {
            validations: {
                type: Object
            }
        },
        watch: {
			trackStatusAndCancellationReasonIdAndStatusMessage(newVal, oldVal) {
				const [oldStatus, oldCancellationReasonId, oldStatusMessage] = oldVal.split('|');
				const [newStatus, newCancellationReasonId, newStatusMessage] = newVal.split('|');

                this.$nextTick(() => {
                    this.$refs.cancelationNote && this.$refs.cancelationNote.focus() // focus on the textarea
                })
                this.setHasDetailsErrors(!this.isDetailsReadyToSave)
			}
        },
        created() {
            this.initDataAndLocation();
            this.initEventDates();
            this.initStatus();
        },
        beforeDestroy() {
            this.setSendCancelationEmail(false);
        },
        computed: {
            ...mapGetters([
                'getCurrentView',
                'getExpandedDay',
                'account',
                'getSelectedEvent',
                'isCreatingSingleEvent',
                'getSelectedEventUpdateFlags',
				'getReasons'
            ]),
            getStatusValue() {
                if (this.getSelectedEvent.status === EventStatus.ACTIVE) {
                    return 'Active';
                } else if (this.getSelectedEvent.status === EventStatus.CANCELED) {
                    return 'Canceled'
                }
            },
            eventStartHour() {
                return [moment(this.getSelectedEvent.start).format('HH'), moment(this.getSelectedEvent.start).format('mm')];
            },
            eventDuration() {
                var timeStart = moment(this.getSelectedEvent.start);
                var timeEnd = moment(this.getSelectedEvent.end);

                var duration = timeEnd.diff(timeStart);

                return [moment.utc(duration).format('HH'), moment.utc(duration).format('mm')];
            },
            eventDurationsInSeconds() {
                let timeArr = this.eventDurationDisplayed.split(":");
                return moment.duration(timeArr[0]+":"+timeArr[1], "HH:mm").asSeconds();
            },
            foundCancellationReasons(){
                return !!this.getReasons && this.getReasons.filter(
					reason => reason.reasonType === 0 || reason.reasonType === 2
				).length > 0;
			},
			filteredCancellationReasons() {
				// Get only reason.reasonType === 0 (all reasons) and reason.reasonType === 2 (event reasons)
				let reasonsAllAndEvent = this.getReasons.filter((reason) => {
					return reason.reasonType === 0 || reason.reasonType === 2;
				})
				
                // Inject other reason (manually) | -9999 type doesn't exist
				reasonsAllAndEvent.push({
					description: 'Other',
					reasonType: -9999,
					id: null
				});

				return reasonsAllAndEvent;
			},
			trackStatusAndCancellationReasonIdAndStatusMessage() {
				return `${this.getSelectedEvent.status}|${this.getSelectedEvent.cancelationReasonId}|${this.getSelectedEvent.statusMessage}`
			},
            isDetailsReadyToSave: function() {
                const optionOther = this.cancellationReasonObject && this.cancellationReasonObject.description || null
                if(this.foundCancellationReasons) {
                    // User selected Active, allow save
                    if (this.getSelectedEvent.status === EventStatus.ACTIVE) return true;
                    // User selected Canceled and select Other and statusMessage is empty, don't allow save
                    if (this.getSelectedEvent.status === EventStatus.CANCELED && optionOther === 'Other' && !this.getSelectedEvent.statusMessage) return false;
                    
                    // Default -> User selected Canceled, allow save if cancelationReasonId or statusMessage is set
                    return !!this.getSelectedEvent.cancelationReasonId || !!this.getSelectedEvent.statusMessage;
                } else {
                    // If we don't have any cancellation reasons, then we can select other, user can save it
                    return true; 
                }
            }
        },
        methods: {
            ...mapActions([
                'openModal',
                'setModalConfirmData',
                'setSendCancelationEmail'
            ]),
            ...eventFormStateHelper.mapActions([
                'setHasPriceErrors',
                'setHasDetailsErrors'
            ]),
            updateEmailNotification(value) {
                this.setSendCancelationEmail(value);
            },
            initDataAndLocation() {
                // Based on the event ending time calculates the duration and stores it
                this.storedEventDuration = moment(this.getSelectedEvent.end).diff(moment(this.getSelectedEvent.start), 'seconds');

                if (this.isCreatingSingleEvent) {
                    this.getProducts();
                }

                // Set eventDurationDisplayed
                if (this.getSelectedEvent.duration) {
                    this.eventDurationDisplayed = this.getSelectedEvent.duration;
                }
            },
            initStatus() {
                this.getPossibleEventStatuse().then((response) => {
                    this.isLoading = false;
                    this.possibleStatusAvailable = response;
                });

                if (this.foundCancellationReasons) {
                    // If cancellationReasonId is not null, then set the cancellationReasonObject based on the id
                    if(!!this.getSelectedEvent.cancelationReasonId) {
                        this.cancellationReasonObject = this.getReasons.find(reason => reason.id == this.getSelectedEvent.cancelationReasonId);
                    }

                    // If canceled and cancellationReasonId is null, then set the cancellationReasonObject to Other reason
                    if (this.getSelectedEvent.status === EventStatus.CANCELED && this.getSelectedEvent.cancelationReasonId === null) {
                        this.cancellationReasonObject = this.filteredCancellationReasons.find(reason => reason.id == this.getSelectedEvent.cancelationReasonId);
                    }
                } else {
                    this.cancellationReasonObject = this.defaultOtherReasonObject;
                }
            },
            initEventDates() {
                // Extract the event hours
                const { start, end } = this.getSelectedEvent;
                const isNewEvent = this.isCreatingSingleEvent;
                const isNewDate = !this.getSelectedEventUpdateFlags.DateLocation;
                const startTime = start.split(' ')[1];
                const endTime = end.split(' ')[1];
                const format = 'YYYY-MM-DD HH:mm:ss';
                let finalDate, startDate, endDate;

                // Validate if is new event
                if (isNewEvent) {
                    // Gets the expanded day (can be the date selected in the calendar)
                    const expandedDate = this.getExpandedDay;

                    if (isNewDate) {
                        // For a new event with a new date, set finalDate according to the view:
                        // If it's ‘agendaDay’, use expandedDate, otherwise, today's date formatted.
                        const todayDate = moment(new Date()).format('YYYY-MM-DD');
                        finalDate = this.getCurrentView === 'agendaDay' ? expandedDate : todayDate;
                    } else {
                        // Para novo evento sem alteração de data, mantém a data original
                        startDate = endDate = expandedDate;
                    }
                } else {
                    // For existing events, it extracts only the date part (YYYY-MM-DD) from the start and end values
                    startDate = start.split(' ')[0];
                    endDate = end.split(' ')[0];
                }

                this.getSelectedEvent.start = moment(`${finalDate || startDate} ${startTime}`).format(format);
                this.getSelectedEvent.end = moment(`${finalDate || endDate} ${endTime}`).format(format);
            },
            setEventStartTime(e){
                // Adding a updateflag to dateTime on editEvent | mixin.js
                this.eventActivateUpdateFlag('DateLocation');
                let timeArr = e.split(":");
                this.getSelectedEvent.start = moment(this.getSelectedEvent.start).set({hour:timeArr[0],minute:timeArr[1]}).format('YYYY-MM-DD HH:mm:ss');
                this.getSelectedEvent.end = moment(this.getSelectedEvent.start).add(this.storedEventDuration, 'seconds').format('YYYY-MM-DD HH:mm:ss');
            },
            setEventStartDate(e) {
                const date = new Date(e);
                const hours = this.getSelectedEvent.start.split(' ')[1];
                const [hour, min, sec] = hours.split(':');

                date.setHours(hour);
                date.setMinutes(min);
                date.setSeconds(sec);
                
                // Adding a updateflag to dateTime on editEvent | mixin.js
                this.eventActivateUpdateFlag('DateLocation');
                
                const format = 'YYYY-MM-DD HH:mm:ss';
                this.getSelectedEvent.start = moment(date).format(format);
                this.getSelectedEvent.end = moment(date).add(this.eventDurationsInSeconds, 'second').format(format);
            },
            setEventEndTime(e){
                // Adding a updateflag to dateTime on editEvent | mixin.js
                this.eventActivateUpdateFlag('DateLocation');
                let timeArr = e.split(":");
                this.eventDurationDisplayed = e;
                this.storedEventDuration = ((timeArr[0] * 3600) + (timeArr[1] * 60));
                this.getSelectedEvent.end = moment(this.getSelectedEvent.start).add(this.eventDurationsInSeconds, 'second').format('YYYY-MM-DD HH:mm:ss');
                this.getSelectedEvent.duration = e +':00';
            },
            getProducts() {
                this.isLoadingProductList = true;
                axios.get('/api/booking/Resume', {
                    params: {
                        supplierId: this.account.supplierId
                    }
                }).then(response => {
                    this.isLoadingProductList = false;
                    if(response.status == 200){
                        this.productList = response.data.productList
                        this.categoryList = response.data.categoryList
                    }
                }).catch(error => {
                    this.isLoadingProductList = false;
                })
            },
            changeStatusSelected(e) {
                if (e.id === 1) {
                    // Adding a updateflag to status on editEvent | mixin.js
                    this.eventActivateUpdateFlag('Status')
                    this.getSelectedEvent.status = this.statusId("Active");
                    this.getSelectedEvent.setToCancel = false;
                    this.getSelectedEvent.cancelationReasonId = null
                    this.cancellationReasonObject = null;
                    this.getSelectedEvent.statusMessage = null
                } else {
                    // Adding a updateflag to status on editEvent | mixin.js
                    this.eventActivateUpdateFlag('Status')
                    this.getSelectedEvent.status = this.statusId("Canceled");
                    this.getSelectedEvent.setToCancel = true;
                    this.cancellationReasonObject = null;
                }
            },
            changeProductSelected() {
                // Adding a updateflag to dateTime on editEvent | mixin.js
                this.eventActivateUpdateFlag('DateLocation');

                // Set max ocupation
                if (!this.getSelectedEvent.maximumLotation || this.getSelectedEvent.maximumLotation == 0 || this.getSelectedEvent.maximumLotation == null) {
                    this.getSelectedEvent.maximumLotation = this.getSelectedEvent.helperProduct.capacity;
                }

                // Set routes
                this.getSelectedEvent.eventRoutes = this.getSelectedEvent.helperProduct.routes;
                // Set product
                this.getSelectedEvent.product = this.getSelectedEvent.helperProduct;
                // Set location
                this.getSelectedEvent.location = !this.getSelectedEvent.location ? this.getSelectedEvent.helperProduct.location : this.getSelectedEvent.location;

                // Set booking question
                if (this.getSelectedEvent.helperProduct.bookingQuestion != null) {
                    this.getSelectedEvent.bookingQuestion = this.getSelectedEvent.helperProduct.bookingQuestion;
                }

                this.getSelectedEvent.bookingQuestionIsActive = this.getSelectedEvent.helperProduct.bookingQuestionIsActive;
                this.getSelectedEvent.bookingQuestionIsMandatory = this.getSelectedEvent.helperProduct.bookingQuestionIsMandatory;
                
                if (this.isCreatingSingleEvent) {   
                    const prices = (this.getSelectedEvent && this.getSelectedEvent.eventGenderPrices) || [];

                    this.getSelectedEvent.shareType = this.getSelectedEvent.helperProduct.productShareType;
                    this.getSelectedEvent.availabilityType = this.getSelectedEvent.helperProduct.availabilityType;

                    const selectedPrincingType = { id: prices[0] && prices[0].genderPriceType };
                    const isReady = isPerOrderAudienceValid(selectedPrincingType, prices);
                    this.setHasPriceErrors(!isReady);
                }
            },
            updateCancellationReason(reason) {
				// Adding a updateflag to status on editEvent
				this.eventActivateUpdateFlag('Status');

				if (reason.id === null) {
                    // Start null. As the user start typing it will have a value
					this.getSelectedEvent.statusMessage = null;
				}
                this.getSelectedEvent.cancelationReasonId = reason.id;
            },
            async getPossibleEventStatuse() {
                try {
                    let response = await axios.get('api/settings/GetEventStatusList');

                    if (response && response.status === 200) {
                        return response.data;
                    }
                }
                catch (error) {
                    console.log(error.response.data.message);
                }
            },
            statusId(statusLabel) {
                for (let status of this.possibleStatusAvailable) {
                    if (statusLabel == status.description) {
                        return status.id;
                    }
                }
            }
        },
        components: {
            Datepicker,
            Timepicker,
            vSelect: extendedVSelect,
            loader,
        }
    }
</script>

<style lang="scss">
    @import "../../../assets/scss/variables";   

    .open-up-cancelation-reason .dropdown-menu {    
        position: absolute;
        left:   0;
        right:  0;
        top:    0; 
        transform: translateY(-100%);
        min-height: 200px;
    }

    .v-select.open-up-cancelation-reason.open .dropdown-toggle { 
        border-top-color: transparent;
        border-top-left-radius: 0;
        border-top-right-radius: 0;

        border-bottom-color: $light;
        border-bottom-left-radius: 4px;
        border-bottom-right-radius: 4px;
    }
</style>



// WEBPACK FOOTER //
// areaDetails.vue?3734f4e8